import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Heart,
  Check,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  RotateCw,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

export const Route = createFileRoute("/flashcards")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Support Cards — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Swipeable, flippable trauma-informed flashcards for grounding, breathing, flashback support, thought reframing, calming and coping. Save favourites and track completion.",
      },
      { property: "og:title", content: "Support Cards — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Mobile-first flashcards across 7 healing categories — swipe, flip, save, search.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/flashcards" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/flashcards" }],
  }),
  component: withMemberGate(FlashcardsPage, "Flashcards"),
});

type Category =
  | "Grounding"
  | "Breathing"
  | "Flashback Support"
  | "Affirmation"
  | "Thought Reframing"
  | "Calming"
  | "Coping Skills"
  | "Movement"
  | "Release"
  | "Awareness"
  | "Regulation";

type Flashcard = {
  id: string;
  title: string;
  category: Category;
  front: string;
  back: string;
  tags: string[];
};

const CATEGORIES: Category[] = [
  "Grounding",
  "Breathing",
  "Flashback Support",
  "Affirmation",
  "Thought Reframing",
  "Calming",
  "Coping Skills",
  "Movement",
  "Release",
  "Awareness",
  "Regulation",
];

const CARDS: Flashcard[] = [
  // Grounding
  {
    id: "g-54321",
    title: "5-4-3-2-1 Grounding",
    category: "Grounding",
    front: "Bring yourself back to the present using your five senses.",
    back: "Name 5 things you SEE • 4 things you can TOUCH • 3 things you HEAR • 2 things you SMELL • 1 thing you TASTE. Go slowly. Speak softly out loud.",
    tags: ["grounding", "anxiety", "panic", "flashback"],
  },
  {
    id: "g-sensory",
    title: "Sensory Awareness",
    category: "Grounding",
    front: "Tune into one sense at a time to anchor in your body.",
    back: "Pick one sense. Spend a full minute noticing only that. Cold water on your hands. The weight of your feet. A scent you love. The sound of your own breath.",
    tags: ["grounding", "sensory", "body"],
  },
  {
    id: "g-safeplace",
    title: "Safe Place Visualisation",
    category: "Grounding",
    front: "Return in your mind to a place that feels safe.",
    back: "Close your eyes. Picture a place — real or imagined — where nothing is asked of you. Notice the light, sounds, temperature. Stay as long as you need.",
    tags: ["grounding", "safety", "visualisation"],
  },
  {
    id: "g-statements",
    title: "Grounding Statements",
    category: "Grounding",
    front: "Speak short reminders out loud to come back to now.",
    back: "I am [your name]. Today is [date]. I am [age] years old. I am in [place]. That was then — this is now. I am safe in this moment.",
    tags: ["grounding", "flashback", "present"],
  },
  // Breathing
  {
    id: "b-finger",
    title: "Finger Breathing",
    category: "Breathing",
    front: "Use one hand to pace a slow, soothing breath.",
    back: "Trace up your thumb as you inhale. Trace down as you exhale. Continue across all five fingers. Repeat until your shoulders soften.",
    tags: ["breathing", "anxiety", "calm"],
  },
  {
    id: "b-star",
    title: "Star Breathing",
    category: "Breathing",
    front: "Trace a 5-point star with your finger as you breathe.",
    back: "Up a point — inhale 4. Down the next side — hold 2. Repeat around the star. Five sides. Two full stars equals one minute.",
    tags: ["breathing", "kids", "calm"],
  },
  {
    id: "b-box",
    title: "Box Breathing",
    category: "Breathing",
    front: "Equal sides: inhale 4, hold 4, exhale 4, hold 4.",
    back: "Trace a box in the air with your finger. Inhale up • hold across • exhale down • hold across. Repeat 4–6 boxes.",
    tags: ["breathing", "focus", "regulation"],
  },
  {
    id: "b-deep",
    title: "Deep Belly Breathing",
    category: "Breathing",
    front: "Soften the belly. Let it rise on the inhale.",
    back: "Hand on belly. Breathe so your hand rises before your chest. In through nose 4, out through mouth 6. The long exhale signals safety.",
    tags: ["breathing", "vagus", "calm"],
  },
  {
    id: "b-reset",
    title: "Nervous System Reset",
    category: "Breathing",
    front: "Short inhale, very long sighing exhale.",
    back: "Inhale through the nose for 3. Open your mouth and sigh the breath out for 8. Repeat 5 times. Notice the wave settling.",
    tags: ["breathing", "panic", "reset"],
  },
  // Flashback Support
  {
    id: "f-trigger",
    title: "Recognise the Trigger",
    category: "Flashback Support",
    front: "Name what is happening: \"I am having a flashback.\"",
    back: "Naming it brings the thinking brain back online. Say silently: This is a memory. The danger is not happening now. My body is reacting to the past.",
    tags: ["flashback", "trauma", "ptsd"],
  },
  {
    id: "f-ground",
    title: "Ground Yourself in the Now",
    category: "Flashback Support",
    front: "Use the body to anchor in the present.",
    back: "Press feet firmly into the floor. Hold something cold. Look around and name 5 objects. Say today's date out loud. You are here. You are safe.",
    tags: ["flashback", "grounding", "present"],
  },
  {
    id: "f-challenge",
    title: "Challenge Negative Thoughts",
    category: "Flashback Support",
    front: "Soften the spiral with a gentler truth.",
    back: "Ask: What would I say to a friend feeling this? Is this thought a fact, or a fear? What is one thing that IS true right now?",
    tags: ["flashback", "thoughts", "cbt"],
  },
  {
    id: "f-support",
    title: "Self-Care & Support",
    category: "Flashback Support",
    front: "Care for yourself after the wave passes.",
    back: "Drink water. Wrap in a blanket. Text a safe person. Eat something small. Rest. You moved through something hard — that took strength.",
    tags: ["flashback", "self-care", "recovery"],
  },
  // Affirmation
  {
    id: "a-positive",
    title: "Positive Self-Talk",
    category: "Affirmation",
    front: "Speak to yourself the way you would to someone you love.",
    back: "I am doing the best I can with what I know today. I am allowed to be a work in progress. My efforts matter, even when they go unseen.",
    tags: ["affirmation", "self-worth", "kindness"],
  },
  {
    id: "a-compassion",
    title: "Self-Compassion",
    category: "Affirmation",
    front: "Offer warmth to the part of you that is struggling.",
    back: "This is a moment of suffering. Suffering is part of being human. May I be kind to myself in this moment. May I give myself what I need.",
    tags: ["affirmation", "self-compassion"],
  },
  {
    id: "a-resilience",
    title: "Resilience Building",
    category: "Affirmation",
    front: "Remind yourself of what you have already survived.",
    back: "I have survived 100% of my hardest days. I am stronger than the storm I am in. Healing is slow — and I am still moving forward.",
    tags: ["affirmation", "resilience", "strength"],
  },
  {
    id: "a-safety",
    title: "Emotional Safety",
    category: "Affirmation",
    front: "Reassure your nervous system that it is safe to feel.",
    back: "It is safe to feel what I feel. My feelings will not destroy me. I can hold this emotion without becoming it. I am safe in this moment.",
    tags: ["affirmation", "safety", "emotion"],
  },
  // Thought Reframing
  {
    id: "t-challenge",
    title: "Challenge Negative Beliefs",
    category: "Thought Reframing",
    front: "Question the old story your mind keeps replaying.",
    back: "What is the evidence for and against this thought? Whose voice does it sound like? Is there a kinder, more accurate truth I can choose instead?",
    tags: ["reframing", "cbt", "beliefs"],
  },
  {
    id: "t-perspective",
    title: "Alternative Perspectives",
    category: "Thought Reframing",
    front: "Try on another way of seeing the same situation.",
    back: "What would a wise friend say? What might be true that I have not considered? If I were to be gentler about this, what would change?",
    tags: ["reframing", "perspective"],
  },
  {
    id: "t-growth",
    title: "Growth Mindset",
    category: "Thought Reframing",
    front: "Add the word \"yet\" to soften an absolute.",
    back: "\"I cannot do this\" becomes \"I cannot do this yet.\" \"I do not feel safe\" becomes \"My body has not learned safety here yet.\" Growth lives in the gap.",
    tags: ["reframing", "growth", "mindset"],
  },
  // Calming
  {
    id: "c-trace",
    title: "Trace and Breathe",
    category: "Calming",
    front: "Use your finger to draw a slow shape as you breathe.",
    back: "Trace a slow spiral on your palm. Inhale as you move outward, exhale as you move inward. The movement and breath together soothe the system.",
    tags: ["calming", "regulation", "anxiety"],
  },
  {
    id: "c-visual",
    title: "Visual Calming",
    category: "Calming",
    front: "Give the eyes something gentle to rest on.",
    back: "Find one calming thing in your space — a plant, sky, candle, soft fabric. Let your eyes rest there for 2 minutes. Soft gaze, soft jaw, soft breath.",
    tags: ["calming", "visual", "soft"],
  },
  {
    id: "c-sensory",
    title: "Sensory Regulation",
    category: "Calming",
    front: "Choose one sensory input that signals safety.",
    back: "Warm tea in your hands. A weighted blanket. A familiar scent. Quiet music. Pick ONE and stay with it. Less is more for an activated system.",
    tags: ["calming", "sensory", "regulation"],
  },
  // Coping Skills
  {
    id: "k-regulation",
    title: "Emotional Regulation",
    category: "Coping Skills",
    front: "Name the feeling to tame the feeling.",
    back: "Pause. Ask: What am I feeling right now? Where do I feel it in my body? What does this feeling need from me? Naming creates space.",
    tags: ["coping", "emotion", "regulation"],
  },
  {
    id: "k-distress",
    title: "Distress Tolerance",
    category: "Coping Skills",
    front: "Ride the wave without making it bigger.",
    back: "TIP: Temperature (cold water on face), Intense exercise (60 sec), Paced breathing (long exhales). Distress passes — your job is to stay safe through it.",
    tags: ["coping", "distress", "dbt"],
  },
  {
    id: "k-anxiety",
    title: "Anxiety Reduction",
    category: "Coping Skills",
    front: "Slow the body so the mind can follow.",
    back: "Exhale longer than you inhale for 2 minutes. Drop your shoulders. Unclench your jaw. The body leads the mind out of anxiety — not the other way round.",
    tags: ["coping", "anxiety", "breathing"],
  },
  {
    id: "k-soothing",
    title: "Self-Soothing",
    category: "Coping Skills",
    front: "Use the five senses to gently care for yourself.",
    back: "SIGHT: something beautiful. SOUND: something gentle. SMELL: something comforting. TASTE: something warm. TOUCH: something soft. Choose one. Linger.",
    tags: ["coping", "self-soothing", "senses"],
  },
  // ───────── Somatic — Movement ─────────
  {
    id: "m-authentic-free",
    title: "Authentic Free Movement",
    category: "Movement",
    front: "Allow your body to move naturally without rules or expectations.",
    back: "Purpose: let movement emerge instead of performing it. Why: supports body awareness and gently releases held tension. Try it: find a comfy position • notice your breath • allow small movements to emerge naturally • follow what feels supportive • continue 2–5 min. Tip: close your eyes if it feels safe. Remember: there is no right or wrong way to move.",
    tags: ["movement", "somatic", "free"],
  },
  {
    id: "m-micro",
    title: "Micro-Movement Exploration",
    category: "Movement",
    front: "The smallest movement counts. Less is more.",
    back: "Purpose: introduce safety through tiny, manageable motion. Why: micro-movements signal to the nervous system that change is non-threatening. Try it: wiggle one finger • shift your gaze softly • flex an ankle • notice the sensation after each. Tip: pause longer than you move. Remember: small steps matter.",
    tags: ["movement", "micro", "gentle"],
  },
  {
    id: "m-joint-circles",
    title: "Slow Joint Circles",
    category: "Movement",
    front: "Gently circle one joint at a time.",
    back: "Purpose: lubricate joints and re-introduce the body. Why: slow circular motion calms the vestibular and proprioceptive systems. Try it: ankles • wrists • shoulders • neck (small half-circles only) • 5 each direction. Tip: stay well within comfort. Remember: safety before intensity.",
    tags: ["movement", "joints", "mobility"],
  },
  {
    id: "m-intuitive-stretch",
    title: "Intuitive Stretch Flow",
    category: "Movement",
    front: "Stretch where your body asks to be stretched.",
    back: "Purpose: trust the body's guidance instead of following a routine. Why: self-led stretching restores agency. Try it: scan the body • notice where it wants length • lean into that stretch for 3 slow breaths • repeat in another spot. Tip: never push past mild sensation. Remember: your body has wisdom.",
    tags: ["movement", "stretch", "intuitive"],
  },
  {
    id: "m-seated-sway",
    title: "Seated Body Sway",
    category: "Movement",
    front: "Sit and let the body sway like a tree in soft wind.",
    back: "Purpose: rhythmic motion to soothe the nervous system. Why: gentle sway activates the parasympathetic response. Try it: sit upright • allow tiny sways left/right or forward/back • match the rhythm of a slow breath • 1–3 minutes. Tip: let your eyes soften. Remember: healing happens gently.",
    tags: ["movement", "rocking", "regulation"],
  },
  {
    id: "m-weight-shifts",
    title: "Standing Weight Shifts",
    category: "Movement",
    front: "Slowly shift weight from one foot to the other.",
    back: "Purpose: rebuild a felt sense of ground. Why: weight transfer activates grounding circuits in the body. Try it: stand softly • shift weight to the left foot • notice • return to centre • shift right • 8–10 slow rounds. Tip: keep knees soft. Remember: move at your own pace.",
    tags: ["movement", "grounding", "balance"],
  },
  {
    id: "m-morning-wake",
    title: "Gentle Morning Wake-Up Movement",
    category: "Movement",
    front: "Wake the body the way you'd wake a friend — slowly, kindly.",
    back: "Purpose: ease into the day without shock. Why: a gentle transition supports nervous-system regulation. Try it: in bed, stretch fingers and toes • roll wrists and ankles • lengthen the spine • sit up slowly • take 3 breaths before standing. Tip: skip alarms when possible. Remember: you are safe in this moment.",
    tags: ["movement", "morning", "wake"],
  },
  {
    id: "m-spine-side",
    title: "Side-to-Side Spine Movement",
    category: "Movement",
    front: "Soft sideways motion through the spine.",
    back: "Purpose: mobilise the spine in a gentle, soothing way. Why: lateral spine movement releases held tension along the body. Try it: stand or sit • gently sway upper body side to side • let arms hang heavy • 1–2 minutes. Tip: small range, slow tempo. Remember: progress is not perfection.",
    tags: ["movement", "spine", "mobility"],
  },
  {
    id: "m-hip-circles",
    title: "Hip Circles & Pelvic Rocking",
    category: "Movement",
    front: "Slow circles and gentle rocks through the hips.",
    back: "Purpose: release tension stored in the pelvis. Why: the pelvis holds a great deal of survival-response energy. Try it: stand softly • draw small circles with your hips • then rock pelvis forward and back • 1–2 min each. Tip: keep it small and curious. Remember: safety before intensity.",
    tags: ["movement", "hips", "release"],
  },
  {
    id: "m-grounded-flow",
    title: "Grounded Standing Flow",
    category: "Movement",
    front: "Stand, breathe, sway, settle — a mini flow.",
    back: "Purpose: a short standing sequence to land in the body. Why: combining stance, breath and motion builds embodied safety. Try it: feet hip-width • sway 30 sec • slow shoulder rolls • soft knee bounces • return to stillness. Tip: end with one full breath. Remember: small steps matter.",
    tags: ["movement", "standing", "flow"],
  },
  {
    id: "m-closing",
    title: "Body-Led Closing Movement",
    category: "Movement",
    front: "Let the body choose how to finish the practice.",
    back: "Purpose: honour the body's pace in closing. Why: self-led endings reinforce autonomy. Try it: ask 'what does my body want to do to finish?' • let one final stretch, sway, or stillness arrive • pause and notice. Tip: thank your body. Remember: there is no right or wrong way.",
    tags: ["movement", "closing", "ritual"],
  },

  // ───────── Somatic — Release ─────────
  {
    id: "r-pmr",
    title: "Progressive Muscle Relaxation",
    category: "Release",
    front: "Tense gently, then release — one body part at a time.",
    back: "Purpose: teach the body the contrast between tension and softness. Why: deliberate release lowers baseline activation. Try it: feet • calves • thighs • belly • hands • shoulders • face — tense 5 sec, release 10 sec. Tip: keep tension mild. Remember: healing happens gently.",
    tags: ["release", "pmr", "relaxation"],
  },
  {
    id: "r-shoulder-melt",
    title: "Shoulder Tension Melt",
    category: "Release",
    front: "Invite your shoulders to soften and drop.",
    back: "Purpose: release a common holding place for trauma tension. Why: the shoulders brace to protect the heart and throat. Try it: inhale, lift shoulders to ears • exhale, let them melt down • repeat 5 slow rounds. Tip: sigh on the exhale. Remember: you are safe in this moment.",
    tags: ["release", "shoulders", "tension"],
  },
  {
    id: "r-shoulder-rolls",
    title: "Shoulder Rolls & Drops",
    category: "Release",
    front: "Roll the shoulders slowly, then let them drop.",
    back: "Purpose: mobilise and release the upper body. Why: rolling motion eases armouring patterns. Try it: roll shoulders forward 5x • backward 5x • finish by lifting and dropping with a soft exhale. Tip: breathe through the whole sequence. Remember: move at your own pace.",
    tags: ["release", "shoulders", "rolls"],
  },
  {
    id: "r-hand-forearm",
    title: "Hand & Forearm Release",
    category: "Release",
    front: "Hands carry the day. Soften them on purpose.",
    back: "Purpose: release habitual gripping in hands and forearms. Why: the hands often hold low-grade survival tension. Try it: open and close hands slowly • shake them out • squeeze gently and release • massage the forearms. Tip: pair with a long exhale. Remember: small steps matter.",
    tags: ["release", "hands", "forearm"],
  },
  {
    id: "r-wrist-finger",
    title: "Wrist & Finger Softening",
    category: "Release",
    front: "Soft circles and stretches for wrists and fingers.",
    back: "Purpose: invite ease into the most expressive parts of the body. Why: softening the periphery soothes the whole nervous system. Try it: wrist circles 5 each way • fan fingers wide • curl and uncurl slowly • gentle thumb-to-finger taps. Tip: keep it tender. Remember: your body has wisdom.",
    tags: ["release", "wrist", "fingers"],
  },

  // ───────── Somatic — Awareness ─────────
  {
    id: "aw-hip-thigh",
    title: "Hip & Thigh Release Awareness",
    category: "Awareness",
    front: "Notice what your hips and thighs are holding.",
    back: "Purpose: bring kind attention to the lower body. Why: awareness is the first step in release. Try it: place hands on thighs • breathe into the hip joints • notice tightness, warmth, sensation • do not try to fix anything. Tip: stay curious, not analytical. Remember: there is no right or wrong way.",
    tags: ["awareness", "hips", "scan"],
  },
  {
    id: "aw-lower-scan",
    title: "Lower Body Softening Scan",
    category: "Awareness",
    front: "Scan from hips to toes, softening as you go.",
    back: "Purpose: settle the lower body and reconnect with the ground. Why: a downward scan calms an activated system. Try it: hips → thighs → knees → calves → ankles → feet • pause at each • invite softness. Tip: let the floor support you. Remember: healing happens gently.",
    tags: ["awareness", "scan", "lower"],
  },
  {
    id: "aw-full-tension",
    title: "Full-Body Tension Check-In",
    category: "Awareness",
    front: "Where is your body holding right now?",
    back: "Purpose: map your current tension landscape with kindness. Why: noticing without judgement reduces shame and reactivity. Try it: close eyes • scan head to feet • silently name 3 places of tension and 1 place of ease. Tip: do not try to change anything yet. Remember: progress is not perfection.",
    tags: ["awareness", "scan", "tension"],
  },
  {
    id: "aw-end-of-day",
    title: "End-of-Day Muscle Reset",
    category: "Awareness",
    front: "A short scan to unload the day from the body.",
    back: "Purpose: signal to your body that the day is done. Why: ending rituals help discharge stored activation. Try it: lie down • take 3 slow breaths • soften jaw, shoulders, belly, hips, legs in turn • rest 1 min. Tip: a sigh helps it land. Remember: you are safe in this moment.",
    tags: ["awareness", "evening", "reset"],
  },

  // ───────── Somatic — Regulation (Rhythm & Tapping) ─────────
  {
    id: "reg-body-drum",
    title: "Body Drumming Patterns",
    category: "Regulation",
    front: "Tap gentle rhythms across the body.",
    back: "Purpose: use rhythm to bring the body online. Why: rhythmic input regulates the nervous system through predictability. Try it: tap chest 4x • thighs 4x • shoulders 4x • repeat at a slow, steady pace 1–2 min. Tip: hum along if you like. Remember: safety before intensity.",
    tags: ["regulation", "rhythm", "drumming"],
  },
  {
    id: "reg-alt-hand",
    title: "Alternating Hand Tapping",
    category: "Regulation",
    front: "Tap left, then right — slow and steady.",
    back: "Purpose: bilateral input to calm and integrate. Why: alternating stimulation engages both hemispheres of the brain. Try it: place hands on thighs • tap left, then right • slow, even pace for 1–3 min. Tip: imagine a soothing word with each tap. Remember: small steps matter.",
    tags: ["regulation", "bilateral", "tapping"],
  },
  {
    id: "reg-knee-knee",
    title: "Knee-to-Knee Rhythm Taps",
    category: "Regulation",
    front: "Tap each knee in a slow, steady rhythm.",
    back: "Purpose: a grounding bilateral rhythm. Why: tapping the lower body draws energy downward and out. Try it: sit comfortably • tap left knee, then right knee • slow tempo • 1–2 min. Tip: pair with a long exhale every 8 taps. Remember: move at your own pace.",
    tags: ["regulation", "tapping", "grounding"],
  },
  {
    id: "reg-side-rock",
    title: "Gentle Side-to-Side Rocking",
    category: "Regulation",
    front: "Soft rocking to soothe like you would a child.",
    back: "Purpose: self-soothing through rhythmic motion. Why: rocking activates the vagus nerve and signals safety. Try it: seated or standing • rock gently side to side • let the breath match the rhythm • 1–2 min. Tip: hug yourself softly. Remember: healing happens gently.",
    tags: ["regulation", "rocking", "soothing"],
  },
  {
    id: "reg-chest-belly",
    title: "Rhythmic Chest & Belly Taps",
    category: "Regulation",
    front: "Soft taps on chest and belly to anchor in the body.",
    back: "Purpose: bring attention to the centre of the body. Why: gentle taps on the heart and belly cue safety and presence. Try it: tap chest 4x • belly 4x • slow steady rhythm • 1–2 min. Tip: place a hand still on the heart at the end. Remember: you are safe in this moment.",
    tags: ["regulation", "tapping", "centre"],
  },
  {
    id: "reg-tap-pause",
    title: "Coordinated Tap-and-Pause",
    category: "Regulation",
    front: "Tap a few times, pause, notice — repeat.",
    back: "Purpose: build the muscle of noticing between rhythms. Why: pausing trains the system to titrate activation. Try it: tap thighs 4x • pause 4 sec • notice • repeat 5 rounds. Tip: let pauses be longer than the taps. Remember: progress is not perfection.",
    tags: ["regulation", "tapping", "pause"],
  },
  {
    id: "reg-cross-body",
    title: "Cross-Body Tapping",
    category: "Regulation",
    front: "Right hand to left shoulder, left hand to right shoulder.",
    back: "Purpose: bilateral, self-soothing 'butterfly hug.' Why: cross-body tapping calms activation and supports integration. Try it: cross arms over chest • tap shoulders alternately • slow, gentle pace • 1–3 min. Tip: breathe slowly throughout. Remember: your body has wisdom.",
    tags: ["regulation", "butterfly", "bilateral"],
  },
];

const FAV_KEY = "rt:flashcards-favs";
const DONE_KEY = "rt:flashcards-done";

function dayIndex() {
  const d = new Date();
  const start = new Date(d.getFullYear(), 0, 0);
  return Math.floor((d.getTime() - start.getTime()) / 86_400_000);
}
function readSet(key: string): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || "[]");
    if (!Array.isArray(raw)) return new Set();
    // Support both legacy string[] and FavoriteRecord[] shapes
    return new Set(
      raw
        .map((x: unknown) =>
          typeof x === "string"
            ? x
            : x && typeof x === "object" && typeof (x as { id?: unknown }).id === "string"
              ? (x as { id: string }).id
              : null,
        )
        .filter((x): x is string => !!x),
    );
  } catch {
    return new Set();
  }
}
function writeFavRecords(
  cards: Flashcard[],
  ids: Set<string>,
) {
  const records = Array.from(ids)
    .map((id) => {
      const c = cards.find((x) => x.id === id);
      return c
        ? { id: c.id, label: c.title, sub: c.category, to: "/flashcards", savedAt: Date.now() }
        : { id, savedAt: Date.now() };
    });
  try {
    localStorage.setItem(FAV_KEY, JSON.stringify(records));
    window.dispatchEvent(
      new CustomEvent("rt-favorites-change", { detail: { key: FAV_KEY } }),
    );
  } catch {
    // ignore quota errors
  }
}
function writeDoneSet(s: Set<string>) {
  localStorage.setItem(DONE_KEY, JSON.stringify(Array.from(s)));
}

function FlashcardsPage() {
  const [favs, setFavs] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<Category | "All">("All");
  const [favsOnly, setFavsOnly] = useState(false);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    setFavs(readSet(FAV_KEY));
    setDone(readSet(DONE_KEY));
  }, []);

  const deck = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CARDS.filter((c) => {
      if (cat !== "All" && c.category !== cat) return false;
      if (favsOnly && !favs.has(c.id)) return false;
      if (q) {
        const hay = `${c.title} ${c.front} ${c.back} ${c.category} ${c.tags.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [cat, favsOnly, favs, query]);

  // Reset idx when deck changes
  useEffect(() => {
    setIdx(0);
    setFlipped(false);
  }, [cat, favsOnly, query]);

  const todayCard = CARDS[dayIndex() % CARDS.length];
  const active = deck[idx];

  function showToast(t: string) {
    setToast(t);
    setTimeout(() => setToast(null), 1600);
  }
  function toggleFav(id: string) {
    const n = new Set(favs);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setFavs(n);
    writeFavRecords(CARDS, n);
    showToast(n.has(id) ? "Saved" : "Removed");
  }
  function markDone(id: string) {
    const n = new Set(done);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setDone(n);
    writeDoneSet(n);
  }
  function go(delta: number) {
    if (deck.length === 0) return;
    setFlipped(false);
    setIdx((i) => (i + delta + deck.length) % deck.length);
  }

  // Swipe handling
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  function onTouchStart(e: React.TouchEvent) {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (startX.current == null || startY.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    const dy = e.changedTouches[0].clientY - startY.current;
    startX.current = null;
    startY.current = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return; // ignore taps/vertical
    if (dx < 0) go(1);
    else go(-1);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-8 sm:py-10">

      <header className="mb-6 mt-4 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" /> Support cards
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground sm:text-4xl">
          Flashcards
        </h1>
        <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground">
          Swipe, flip, and learn one small healing skill at a time. Tap a card to flip.
          Swipe left or right (or use the arrows) to move through the deck.
        </p>
      </header>

      {/* Daily pick */}
      <div className="mb-4 rounded-2xl border border-gold/30 bg-gradient-to-br from-gold-light/30 to-blush/20 p-3 text-center text-xs text-muted-foreground sm:text-sm">
        Today's recommendation:{" "}
        <button
          onClick={() => {
            const i = deck.findIndex((c) => c.id === todayCard.id);
            if (i >= 0) {
              setIdx(i);
              setFlipped(false);
            } else {
              setCat("All");
              setFavsOnly(false);
              setQuery("");
              setTimeout(() => {
                const j = CARDS.findIndex((c) => c.id === todayCard.id);
                setIdx(j);
                setFlipped(false);
              }, 0);
            }
          }}
          className="font-medium text-foreground underline-offset-2 hover:underline"
        >
          {todayCard.title}
        </button>{" "}
        · {todayCard.category}
      </div>

      {/* Search + filters */}
      <div className="mb-4 space-y-2">
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search flashcards (try 'panic', 'flashback', 'sleep'…)"
            className="w-full rounded-full border border-border bg-background py-2.5 ps-10 pe-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-gold/40"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute end-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:text-foreground"
              aria-label="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Chip active={cat === "All"} onClick={() => setCat("All")} label="All" />
          {CATEGORIES.map((c) => (
            <Chip key={c} active={cat === c} onClick={() => setCat(c)} label={c} />
          ))}
          <Chip
            active={favsOnly}
            onClick={() => setFavsOnly((f) => !f)}
            label={`♥ Favourites${favs.size ? ` (${favs.size})` : ""}`}
          />
        </div>
      </div>

      {/* Card stage */}
      {deck.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border bg-card py-16 text-center text-sm text-muted-foreground">
          No flashcards match. Try clearing filters or searching for something else.
        </div>
      ) : (
        <>
          <div
            className="relative mx-auto h-[360px] w-full max-w-md select-none sm:h-[420px]"
            style={{ perspective: "1200px" }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <button
              type="button"
              onClick={() => setFlipped((f) => !f)}
              className="relative h-full w-full rounded-3xl text-start transition-transform duration-500"
              style={{
                transformStyle: "preserve-3d",
                transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
              }}
              aria-label={`Flashcard: ${active.title}. Tap to flip.`}
            >
              <CardFace
                card={active}
                side="front"
                done={done.has(active.id)}
                fav={favs.has(active.id)}
              />
              <CardFace
                card={active}
                side="back"
                done={done.has(active.id)}
                fav={favs.has(active.id)}
              />
            </button>

            {/* Side arrows (desktop / tablet) */}
            <button
              onClick={() => go(-1)}
              aria-label="Previous card"
              className="absolute -start-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-border bg-background p-2 shadow-sm hover:bg-secondary sm:block"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next card"
              className="absolute -end-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-border bg-background p-2 shadow-sm hover:bg-secondary sm:block"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Position + counter */}
          <div className="mt-4 text-center text-xs text-muted-foreground">
            Card {idx + 1} of {deck.length} · {done.size}/{CARDS.length} completed
          </div>

          {/* Controls (mobile-first) */}
          <div className="mt-4 grid grid-cols-4 gap-2 sm:flex sm:flex-wrap sm:justify-center">
            <CtrlBtn onClick={() => go(-1)} icon={ChevronLeft} label="Prev" />
            <CtrlBtn onClick={() => setFlipped((f) => !f)} icon={RotateCw} label="Flip" />
            <CtrlBtn
              onClick={() => toggleFav(active.id)}
              icon={favs.has(active.id) ? Check : Heart}
              label={favs.has(active.id) ? "Saved" : "Save"}
              accent={favs.has(active.id)}
            />
            <CtrlBtn
              onClick={() => markDone(active.id)}
              icon={CheckCircle2}
              label={done.has(active.id) ? "Done" : "Mark done"}
              accent={done.has(active.id)}
            />
            <CtrlBtn onClick={() => go(1)} icon={ChevronRight} label="Next" />
          </div>
        </>
      )}

      {toast && (
        <div className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex justify-center sm:bottom-8">
          <div className="rounded-full bg-foreground px-4 py-2 text-xs font-medium text-background shadow-lg">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

function CardFace({
  card,
  side,
  done,
  fav,
}: {
  card: Flashcard;
  side: "front" | "back";
  done: boolean;
  fav: boolean;
}) {
  const isBack = side === "back";
  return (
    <div
      className={`absolute inset-0 flex flex-col rounded-3xl border p-6 shadow-md sm:p-8 ${
        isBack
          ? "border-foreground/20 bg-gradient-to-br from-blush/30 via-card to-gold-light/30"
          : "border-gold/30 bg-gradient-to-br from-gold-light/40 via-card to-blush/20"
      }`}
      style={{
        backfaceVisibility: "hidden",
        transform: isBack ? "rotateY(180deg)" : undefined,
      }}
    >
      <div className="mb-3 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-background/70 px-2 py-1">
          {card.category}
        </span>
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          {fav && <Heart className="h-3 w-3 fill-rose-500 text-rose-500" />}
          {done && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
          {isBack ? "Back" : "Front"}
        </span>
      </div>
      <h2 className="font-display text-xl text-foreground sm:text-2xl">{card.title}</h2>
      <p className="mt-4 flex-1 text-base leading-relaxed text-foreground sm:text-lg">
        {isBack ? card.back : card.front}
      </p>
      <div className="mt-4 text-center text-[11px] text-muted-foreground">
        Tap card to {isBack ? "see front" : "reveal practice"} · swipe to change card
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border bg-card text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function CtrlBtn({
  onClick,
  icon: Icon,
  label,
  accent,
}: {
  onClick: () => void;
  icon: typeof Heart;
  label: string;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex flex-col items-center justify-center gap-1 rounded-2xl border px-2 py-2.5 text-[11px] font-medium transition-colors sm:flex-row sm:gap-1.5 sm:px-4 sm:py-2 sm:text-xs ${
        accent
          ? "border-rose-400/50 bg-rose-50 text-rose-700"
          : "border-border bg-background text-foreground hover:bg-secondary"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
